/**
 * User Permissions Hook
 * Handles role-based permissions for the application
 */

import { useState, useEffect, useCallback } from "react";
import { useUser } from "../contextapi/UserContext";
import { offlineSupabase } from "../utils/offlineSupabaseProxy";

export interface UserPermissions {
  // Global permissions
  isGuest: boolean;
  isOrgAdmin: boolean;
  isOrgMember: boolean;
  isPlatformAdmin: boolean;
  platformAdminLevel?: string;
  
  // Team permissions
  teamPermissions: {
    [teamId: string]: {
      isGroupAdmin: boolean;
      isGroupMember: boolean;
      canManageTeam: boolean;
      canViewTeam: boolean;
    };
  };
  
  // Feature permissions
  canEditProfile: boolean;
  canViewOrganizations: boolean;
  canEditOrganizations: boolean;
  canManageTeams: boolean;
  canCreateTeams: boolean;
  canEditBranding: boolean;
  canViewSecurity: boolean;
  canEditSecurity: boolean;
  canViewSubscription: boolean;
  canEditSubscription: boolean;
  canEditPreferences: boolean;
}

export interface UserRoleData {
  profileRole: string | null;
  organizationRole: string | null;
  organizationId: string | null;
  platformAdminLevel: string | null;
  teamRoles: Array<{
    teamId: string;
    teamName: string;
    role: string;
  }>;
}

export function useUserPermissions() {
  const { user } = useUser();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [roleData, setRoleData] = useState<UserRoleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserRoles = useCallback(async () => {
    console.log("🚀 fetchUserRoles called with user:", { userId: user?.id, userEmail: user?.email });

    if (!user?.id) {
      console.log("❌ No user ID found, stopping fetch");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log("🔄 Starting role fetch for user:", user.id);

      // Use the RLS-bypassing function to get all user permissions data
      console.log("📋 Fetching user permissions via function for user:", user.id);
      const { data: permissionsData, error: permissionsError } = await offlineSupabase
        .rpc("get_user_permissions", { user_id_param: user.id });

      if (permissionsError) {
        console.error("❌ Permissions fetch error:", permissionsError);
        throw permissionsError;
      }

      console.log("✅ Permissions data fetched:", permissionsData);

      // Extract data from the function result
      const profile = permissionsData.user_context?.profile;
      const orgMemberships = permissionsData.user_context?.organizations || [];
      const teamMemberships = permissionsData.user_context?.teams || [];
      const platformAdmin = permissionsData.user_context?.platform_admin;
      
      console.log('[useUserPermissions] Extracted data:', {
        profile,
        orgMemberships,
        teamMemberships,
        platformAdmin,
        userContext: permissionsData.user_context
      });

      // Get the primary organization membership
      // The get_user_permissions function already orders by default workspace preference
      let orgMembership = null;
      if (orgMemberships && orgMemberships.length > 0) {
        // Take the first membership (which is the default workspace due to our ordering)
        orgMembership = orgMemberships[0];
      }

      const roleData: UserRoleData = {
        profileRole: profile?.role || null,
        organizationRole: orgMembership?.role || null,
        organizationId: orgMembership?.organisation_id || null,
        platformAdminLevel: platformAdmin?.admin_level || null,
        teamRoles: (teamMemberships || []).map(tm => ({
          teamId: tm.group_id,
          teamName: tm.group_name,
          role: tm.role,
        })),
      };

      setRoleData(roleData);
      const calculatedPermissions = calculatePermissions(roleData);

      // Debug logging
      console.log("🔍 User Permissions Debug:", {
        userId: user.id,
        rawPermissionsData: permissionsData,
        extractedProfile: profile,
        extractedOrgMemberships: orgMemberships,
        extractedTeamMemberships: teamMemberships,
        extractedPlatformAdmin: platformAdmin,
        selectedOrgMembership: orgMembership,
        finalRoleData: roleData,
        calculatedPermissions,
        timestamp: new Date().toISOString()
      });
      
      console.log("🔄 useUserPermissions hook updated with new roleData:", {
        organizationId: roleData.organizationId,
        organizationRole: roleData.organizationRole,
        profileRole: roleData.profileRole
      }); 

      setPermissions(calculatedPermissions);
    } catch (err) {
      console.error("❌ Error fetching user roles:", err);
      console.error("❌ Error details:", {
        message: err instanceof Error ? err.message : "Unknown error",
        userId: user?.id,
        userEmail: user?.email
      });
      setError(err instanceof Error ? err.message : "Failed to fetch user roles");
      setRoleData(null);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const calculatePermissions = (roleData: UserRoleData): UserPermissions => {
    const isGuest = roleData.profileRole === "guest" || !roleData.organizationRole;
    const isOrgAdmin = roleData.organizationRole === "org_admin";
    const isOrgMember = roleData.organizationRole === "org_member";
    const isPlatformAdmin = !!roleData.platformAdminLevel;
    const isSuperAdmin = roleData.platformAdminLevel === "super";

    // Calculate team permissions
    const teamPermissions: UserPermissions["teamPermissions"] = {};
    roleData.teamRoles.forEach(teamRole => {
      const isGroupAdmin = teamRole.role === "group_admin";
      const isGroupMember = teamRole.role === "group_member" || isGroupAdmin;
      
      teamPermissions[teamRole.teamId] = {
        isGroupAdmin,
        isGroupMember,
        canManageTeam: isGroupAdmin || isOrgAdmin,
        canViewTeam: isGroupMember || isOrgAdmin,
      };
    });

    return {
      // Global permissions
      isGuest,
      isOrgAdmin,
      isOrgMember,
      isPlatformAdmin,
      platformAdminLevel: roleData.platformAdminLevel,
      teamPermissions,

      // Feature permissions based on role hierarchy (Platform Admin overrides all)
      canEditProfile: true, // Everyone can edit their profile

      canViewOrganizations: !isGuest || isPlatformAdmin,
      canEditOrganizations: isOrgAdmin || isPlatformAdmin,

      canManageTeams: isOrgAdmin || isOrgMember || isPlatformAdmin,
      canCreateTeams: isOrgAdmin || isOrgMember || isPlatformAdmin,

      canEditBranding: isOrgAdmin || isOrgMember || isPlatformAdmin,

      canViewSecurity: isOrgAdmin || isPlatformAdmin,
      canEditSecurity: isOrgAdmin || isPlatformAdmin,

      canViewSubscription: isOrgAdmin || isOrgMember || isPlatformAdmin,
      canEditSubscription: isOrgAdmin || isPlatformAdmin,

      canEditPreferences: true, // Everyone can edit preferences
    };
  };

  const refreshPermissions = useCallback(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  const hasTeamPermission = useCallback((teamId: string, permission: keyof UserPermissions["teamPermissions"][string]) => {
    if (!permissions?.teamPermissions[teamId]) return false;
    return permissions.teamPermissions[teamId][permission];
  }, [permissions]);

  const canManageSpecificTeam = useCallback((teamId: string) => {
    return hasTeamPermission(teamId, "canManageTeam");
  }, [hasTeamPermission]);

  useEffect(() => {
    fetchUserRoles();
  }, [fetchUserRoles]);

  return {
    permissions,
    roleData,
    loading,
    error,
    refreshPermissions,
    hasTeamPermission,
    canManageSpecificTeam,
  };
}
